import { action, computed, makeAutoObservable, observable } from "mobx";
import OptionModel from "../models/OptionModel";
import ProductConfigurationOption, { MATTRESS_HARDNESS_OPTION_ID, MATTRESS_SIZE_OPTION_ID } from "../models/ProductConfigurationOption";
import { StoreClass } from "./Store";
import { toBoolean } from "../utils/Booleans";
import { clamp } from "../utils/Numbers";
import { apiAdapter } from "../adapters/ApiAdapter";
import { _t } from "../utils/Lang";
import { MATTRESS_WITH_BED_OPTION_ID, MATTRESS_WITH_BED_DISCOUNT } from "../utils/ConfigurationOptions";


 
class ProductDataType {
    id: number;
    name: string;
    can_buy: boolean;
    productExists30DaysBeforePromotion: boolean;
    weight: {
        weight_float: number;
        weight: string;
    };
    newProduct: boolean;
    shortDescription: string;
    description: string;
    options_configuration: Array<{
        required: boolean;
        stock: boolean;
        values: Array<{
            id: number;
            order: number;
            name: string;
            ch_extra_data: {
                items: Array<{
                    id: number;
                    name: string;
                    item_group: {
                        id: number;
                        name: string;
                    };
                    productOptions: Array<{
                        optionId: number;
                        valueId: number;
                    }>;
                    attributes: {
                        width?: string | null;
                    };
                }>;
            };
        }>;
        id: number;
        name: string;
        type: string;
        order: number;
    }>;
    lowest_shipping_cost: null | number;
    historical_lowest_price: string;
    net_historical_lowest_price: string;
    ch_extra_data: {
        variants: Array<{
            price: string;
            optionValues: Array<{
                option: number;
                value: number;
            }>;
        }>;
        items: Array<{
            id: number;
            name: string;
            item_group: {
                id: number;
                name: string;
            };
            product: {
                id: number;
            };
            attributes: {
                image: Array<string>;
                height: string;
                mattress_for_whom: Array<string>;
                learn_more: string | null;
                badge: string | null;
            };
        }>;
    };
}



export default class ProductModel {
    store: StoreClass;
    owner: ProductModel = null;
    @observable id;
    @observable valueId;
    /**
     * Product id in Content Hub
     */
    @computed get productId() {
        return this.getExtraData('id');
    }
    @observable optionId;
    @observable basePrice;
    @observable name;
    @observable stockId;
    @observable inCart = false;
    @observable cartId = false;
    @observable data: ProductDataType = {};
    @observable options_configuration: ProductConfigurationOption[] = [];
    constructor({ store, owner,  ...props }) {
        makeAutoObservable(this);
        this.optionsData = observable.map([]);
        this.optionsData2 = observable.map([]);
        this.store = store;
        this.owner = owner;
        if (!store) {
            throw Error(`store is undefined`);
        }

        this.fromJSON(props);
    }

    @computed get description() {
        return this.data?.shortDescription;
    }

    /** 
     * Enable quantity selection for product in upsell popup
     * */
    @computed get enable_quantity_selection() {
        return toBoolean(this.getExtraDataAttribute("enable_quantity_selection"));
    }
    
    @observable sizes:ProductModel[] = [];
    @computed get hasSizes():Boolean{
        return this.sizes.length > 0;
    }
    @computed get maxSizes() {
        return 3;
    }
    /**
     * Main product reprezents the first size.
     */
    @computed get canAddMoreSizes() {
        return this.sizes.length < this.maxSizes;
    }
    @computed get allSizesProducts():ProductModel[] {
        return this.sizes;
    }
    @action addSize() {
        const clone = this.clone({owner: this});
       
        this.sizes.push(clone);
    }
    @action removeSize(index) {
        console.log('removeSize', index);
        this.sizes = this.sizes.filter((_, i) => i !== index);
    }

    /**
     * Gets the upsell category from the product extra data.
     * @returns {{"id": 919,"name": "Zestawy","item_group": {"id": 43,"name": "Upsell - Kategorie"},"attributes": []} | null} The upsell category or null if it does not exist.
     */
    @computed get upsell_category() {
        return this.getExtraDataAttribute("upsell_category")?.length > 0 ? this.getExtraDataAttribute("upsell_category")[0] : null;
    }
    @computed get upsell_category_id() {
        return this.upsell_category?.id;
    }


    @computed get can_buy() {
        return this.data.can_buy;
    }
   

    @computed get height() {
        return this.getExtraDataAttribute("height");
    }
    @computed get topper_height() {
        return this.getExtraDataAttribute("topper_height");
    }
    @computed get size() {
        return this.getExtraDataAttribute("size");
    }
    @computed get priority() {
        return this.getExtraData("priority");
    }
    @computed get badgeText() {
        return this.getExtraDataAttribute("badge");
    }
    @computed get imageUrl() {
        var images = this.getExtraDataAttribute("image");
        if (images?.length > 0) {
            return images.at(0);
        }
        return null;
    }
    @computed get image() {
        return this.imageUrl;
    }
    @computed get imageFull() {
        return this.getExtraDataAttribute("full_image");
    }

    @computed get isProductOption() {
        return Boolean(this.optionId);
    }


    @computed get attributes() {
        var attributes = this.getExtraDataAttribute("features") || [];
        if(this.height){
            attributes.push(
                { text: _t('height_value', { value: this.height }), icon: 'height' }
            );
        }
        if(this.topper_height){
            attributes.push(
                { text: _t('height_value', { value: this.topper_height }), icon: 'height' }
            );
        }
        if(this.size){
            attributes.push(
                { text: _t('size_value', { value: String(this.size).replaceAll('cm', '') }), icon: 'height' }
            );
        }
        return attributes;
    }

    @computed get learn_more() {
        return this.getExtraDataAttribute("learn_more");
    }
        @computed get add_legs_to_height() {
        var val = this.getExtraDataAttribute("add_legs_to_height", false);
        if(val){
            return Boolean(val);
        }
        return false;
    }

    @computed get features() {
        return this.getExtraDataAttribute("features") || [];
    }
    @computed get mattress_for_whom() {
        return this.getExtraDataAttribute("mattress_for_whom") || [];
    }
    @computed get comfort_zones_bullets(): string[] {
        return this.getExtraDataAttribute("comfort_zones_bullets") || [];
    }
    @computed get comfort_zones_text(): string {
        return this.getExtraDataAttribute("comfort_zones_text") || '';
    }
    @computed get mattress_features(): string[] {
        return this.getExtraDataAttribute("mattress_features") || [];
    }



    @computed get extraOptions(){
        return this.getExtraDataAttribute('extra_options') || [];
    }
    /**
     * If emoty array is returned, it means that there are no filtering done.
     * If there ere items id only items with those ids will be returned.
     */
    @computed get available_mattresses(){
        return this.getExtraDataAttribute('available_mattresses') || [];
    }
    @computed get extraOptionsIds(){
        return this.extraOptions.map(option => option.id);

    }
    @computed get url(){
        return this.data.url;

    }
    @computed get categoryName(){
        return this.data.category?.name;
    }
    @computed get brandName(){
        return this.data.producer?.name;
    }

    @computed get isAvailable(){
        return true;
    }

    hasConfigurationOption(optionId) {
        return this.options_configuration.some(
            (option) => option.id === optionId
        );
    }

    /**
     * MYB-144: the product defines the "mattress in a set with the bed" option,
     * so it takes part in the 10% discount when bought from the configurator.
     */
    @computed get hasMattressWithBedOption() {
        return this.hasConfigurationOption(MATTRESS_WITH_BED_OPTION_ID);
    }

    /**
     * MYB-144: price actually charged for the product. A mattress sold with the
     * bed gets 10% off its full configured price; every other product keeps its
     * regular price.
     */
    @computed get discountedPrice() {
        if (this.hasMattressWithBedOption) {
            return this.price * (1 - MATTRESS_WITH_BED_DISCOUNT);
        }
        return this.price;
    }

    getExtraData(name) {
        if (
            this.data.ch_extra_data?.items !== undefined && this.data.ch_extra_data?.items.length > 0 &&
            this.data.ch_extra_data.items[0][name] !== undefined
        ) {
            return this.data.ch_extra_data.items[0][name];
        }
    }
    getExtraDataAttribute(name, _default = null) {
        const attributes = this.getExtraData("attributes");
        if (attributes && attributes[name] !== undefined) {
            return attributes[name];
        }
        return _default;
    }

    prepareOptions(options) {
        return (options || []).map((option) => {
            return new ProductConfigurationOption({ product: this, ...option });
        });
    }
    @action preselectDefaults() {
        this.options_configuration.forEach((option) => {
            if (option.options?.length > 0) {
                option.value = option.options[0].valueId;
            }
        });

        if(this.sizes.length > 0){
            this.sizes.forEach((size) => {
                size.preselectDefaults();
            })
        }
    }


    @computed get areRequiredOptionsSelected() {
        return this.options_configuration
        .filter((option) => option.required)
        .every((option) => {
            // every that is required has selectedOption
            return option.selectedOption;
        });
    }


    @observable optionsData;
    @observable optionsData2;
    getDataStore(index = 1) {
        if (index === 2) {
            // return this.optionsData;
            return this.store.selectedMattress.at(index - 1);
        }
        return this.store.selectedMattress.at(index - 1);
        // return this.optionsData2;
    }
    updateOption(optionId, valueId, index = 1) {
        // const store = this.getDataStore(index);
        if(Array.isArray(valueId)){
            throw Error('valueId is an array');
        }
        const conf_option = this.options_configuration.find((o) => o.id === optionId);
        conf_option.value = valueId;
        console.log('Updated option', {optionId, valueId, product: this});
    }
    getOptionValue(optionId, index = 1) {
        // const store = this.getDataStore(index);
        const conf_option = this.options_configuration.find((o) => o.id === optionId);
        return conf_option?.value;
    }

    getOptionsByOptionId(optionId) {
        const amount = this.store.mattressAmount;
        const options = this.options_configuration.find((o) => o.id === optionId)?.options || [];
        if (optionId === MATTRESS_SIZE_OPTION_ID) {
            
            if (amount === 1) {
                return options.filter((o) => o.isFullSize);
            }
            return options.filter((o) => o.isHalfSize);
        }

        return options;
        
    }
    hasMatchingVariant(optionId, valueId) {
        return this.variants.some((v) => v.option === optionId && v.value === valueId);
    }
    logOptions() {
        console.log(this.options_configuration.map((o) => {
            return {
                optionId: o.id,
                value: o.value
            }
        }));
    }

    @computed get variants():Array<{price: number, option: number, value: number}> {
        return this.data?.ch_extra_data?.variants.map(v => ({price: parseFloat(v.price), option: v.optionValues[0].option, value: v.optionValues[0].value })) || [];
    }

    @computed get selectedVariant(){

        for (const o of this.options_configuration) {
            for (const variant of this.variants) {
                if (o && o.value === variant.value && o.id === variant.option) {
                    return variant;
                }   
            }
        }

        return null;
        
    }

    @computed get selectedVariantPrice(){

        if(this.selectedVariant){
            return parseFloat(this.selectedVariant.price);
        }

        return null;
    }

    @computed get optionsPrice(){
        return this.options_configuration.reduce((acc, option) => {
            if(option.selectedOption?.price){
                acc += option.selectedOption.price;
            }
            return acc;
        }, 0);
    }

    @computed get price() {
        if(this.upsell_category && this.hasSizes){
            return this.sizes.reduce((acc, size) => {
                acc += size.price;
                return acc;
            }, 0);
        }


        const optionsPrice = this.optionsPrice;
        if(this.selectedVariantPrice){
            return this.selectedVariantPrice + optionsPrice;
        }
        return this.basePrice + optionsPrice;
    }

    /**
     * Price including options for the variant matching any of the given size entries.
     * Falls back to the currently selected/default price when none matches.
     * @param {Array<{optionId:number, valueId:number}>} sizes
     * @returns {number}
     */
    priceForSizes(sizes = []) {
        for (const { optionId, valueId } of sizes) {
            const variant = this.variants.find(
                (v) => v.option === optionId && v.value === valueId
            );
            if (variant) {
                return variant.price + this.optionsPrice;
            }
        }
        return this.price;
    }

    @computed get deliveryDays() {
        if(this.data?.delivery?.name){
            return this.data.delivery.name;
        }
        return null;
    }
    @computed get deliveryHours() {
        if(this.data?.delivery?.hours){
            return parseInt(this.data.delivery.hours);
        }
        return null;
    }

    /**
     * @returns {number} The sum of all prices of all sizes, or
     * the price of the product multiplied by its quantity if it is not a product with sizes.
     */
    @computed get priceSum() {
        if(this.upsell_category && this.hasSizes){
            return this.sizes.reduce((acc, size) => {
                acc += size.price * size.quantity;
                return acc;
            }, 0);
        }

        return this.price * this.quantity;
    }

    @computed get itemName(){
        return this.getExtraData("name");
    }
    @computed get subtitle(){
        return this.getExtraDataAttribute("subtitle");
    }

    /**
     *
     * @param {ProductDataType} props
     */
    fromJSON(props) {
        try {
            
            this.id = parseInt(props.id);
            this.valueId = this.id;
            this.optionId = null;
            this.data = props;
            this.name = props.name;
            if(this.itemName){
                this.name = this.itemName;
            }
            
            
            this.basePrice = parseFloat(props.price.gross?.final_float);
            this.stockId = props.stockId;
            
            this.options_configuration = this.prepareOptions(
                this.data.options_configuration
            );

            if(this.options_configuration.length > 0  && !this.owner){
                if(this.sizes.length === 0){
                    this.addSize();
                }
            }

        } catch (err) {
            console.error({ err, props });
            debugger;
            throw err;
        }
    }

    toPayload() {
        return {
            id: this.id,
            options: this.toOptions()
        }
    }

    async addToBasket(){
        return apiAdapter.addToBasket({
            stock_id: this.stockId,
            product_id: this.id,
            options: { // only if product has variants
                1: 1 // option_id : value_id
            }
        });
    }

    toString() {
        return `ProductModel: ${this.id} - ${this.name}`;
    }
    toOptions() {
        const options = this.options_configuration.reduce((acc, option) => {
            acc[option.id] = option.value;
            return acc;
        }, {});
        // MYB-144: a mattress sold from the configurator is always part of a bed
        // set, so always opt into the "mattress in a set with the bed" discount.
        if (this.hasMattressWithBedOption) {
            options[MATTRESS_WITH_BED_OPTION_ID] = true;
        }
        return options;
    }
    toCartPayload() {
        return {
            stock_id: this.stockId,
            options: this.toOptions(),
            product_id: this.id
        }
    }
    setOptions(options) {
        this.options_configuration.forEach((option) => {
            if(options[option.id] !== undefined){
                option.value = options[option.id];
            }
        });
    }

    @observable quantity = 1;
    @action updateQuantity(q) {
        this.quantity = clamp(q, 1, 99);
    }



    clone(props){
        var options = this.toOptions();
        var p = new ProductModel({store: this.store, ...this.data, ...props});
        p.setOptions(options);
        return p;
    }
}

